import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {type UseEmblaCarouselType} from 'embla-carousel-react'
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon'
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon'

import {MainIconButton} from '@festi/common/components/buttons'

type CarouselApi = UseEmblaCarouselType[1]
type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean
  nextBtnDisabled: boolean
  onPrevButtonClick: () => void
  onNextButtonClick: () => void
}

export const usePrevNextButtons = (
  emblaApi: CarouselApi | undefined,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())

    const slides = emblaApi.slideNodes()

    emblaApi.slidesInView().forEach((indexInView) => {
      slides[indexInView].removeAttribute('aria-hidden')
    })
    emblaApi.slidesNotInView().forEach((indexNotInView) => {
      slides[indexNotInView].setAttribute('aria-hidden', 'true')
    })
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  }
}

type PropType = ComponentPropsWithRef<'button'>

export const PrevButton: React.FC<PropType> = (props) => {
  return (
    <MainIconButton
      buttonVariant="ghost"
      icon={ArrowLeftSLineIcon}
      aria-label="previous"
      {...props}
    />
  )
}

export const NextButton: React.FC<PropType> = (props) => {
  return (
    <MainIconButton
      buttonVariant="ghost"
      icon={ArrowRightSLineIcon}
      aria-label="next"
      {...props}
    />
  )
}
